import React from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useGetPartReplacementDetailsQuery } from '../../../../api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import PartReplacementDetailsFilter from './PartReplacementDetailsFilter';
import { useNavigate } from 'react-router-dom';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import { formatDateForApi } from '../../../../utils/dateUtils';

export default function PartReplacementDetailsDialog({ visible, onHide, itemCode, chassisNumber, isDealer }) {
    const { th, getCurrentLanguageId } = useLanguage();
    const navigate = useNavigate();

    const [page, setPage] = React.useState(1);
    const [rows, setRows] = React.useState(10);

    const [filters, setFilters] = React.useState({
        chassisNumber: chassisNumber || undefined,
        dealerId: undefined,
        claimDate: undefined,
        failureDate: undefined,
        failureStartDate: undefined,
        failureEndDate: undefined,
        status: undefined,
        models: undefined,
        countryCode: undefined,
        isScrap: undefined,
        warrantyType: undefined
    });

    React.useEffect(() => {
        if (visible) {
            setPage(1);
            setRows(10);
            setFilters((prev) => ({
                ...prev,
                chassisNumber: chassisNumber || undefined
            }));
        }
    }, [visible, itemCode, chassisNumber]);

    const queryArgs = React.useMemo(
        () => ({
            pageNumber: page,
            pageSize: rows,
            itemCode: itemCode,
            chassisNumber: filters.chassisNumber || undefined,
            dealerId: filters.dealerId ?? undefined,
            claimDate: formatDateForApi(filters.claimDate) || undefined,
            failureDate: formatDateForApi(filters.failureDate) || undefined,
            failureStartDate: formatDateForApi(filters.failureStartDate) || undefined,
            failureEndDate: formatDateForApi(filters.failureEndDate) || undefined,
            status: filters.status ?? undefined,
            models: filters.models ?? undefined,
            countryCode: filters.countryCode ?? undefined,
            isScrap: filters.isScrap ?? undefined,
            warrantyType: filters.warrantyType ?? undefined,
            languageId: getCurrentLanguageId()
        }),
        [page, rows, itemCode, filters, getCurrentLanguageId]
    );

    const { data, isFetching } = useGetPartReplacementDetailsQuery(queryArgs, {
        skip: !visible || !itemCode
    });

    const payload = data?.data;
    const items = Array.isArray(payload?.items) ? payload.items : [];
    const total = payload?.totalCount ?? 0;

    const onPageChange = (e) => {
        setPage(e.page + 1);
        setRows(e.rows);
    };

    const [sortField, setSortField] = React.useState(null);
    const [sortOrder, setSortOrder] = React.useState(0);

    const sortedData = React.useMemo(() => {
        if (!sortField || sortOrder === 0) return items;

        const isDateKey = (key) => /date/i.test(key);
        return [...items].sort((a, b) => {
            let av = a?.[sortField];
            let bv = b?.[sortField];

            if (av == null && bv == null) return 0;
            if (av == null) return sortOrder;
            if (bv == null) return -sortOrder;

            const looksLikeDate = isDateKey(sortField) || (typeof av === 'string' && /^\d{4}-\d{2}-\d{2}/.test(av)) || (typeof bv === 'string' && /^\d{4}-\d{2}-\d{2}/.test(bv)) || av instanceof Date || bv instanceof Date;

            if (looksLikeDate) {
                const da = av instanceof Date ? av : new Date(av);
                const db = bv instanceof Date ? bv : new Date(bv);
                return sortOrder * (da.getTime() - db.getTime());
            }

            if (typeof av === 'number' && typeof bv === 'number') {
                return sortOrder * (av - bv);
            }

            return sortOrder * String(av).localeCompare(String(bv), 'tr', { numeric: true, sensitivity: 'base' });
        });
    }, [items, sortField, sortOrder]);

    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    };

    const formatDate = (v) => {
        const d = typeof v === 'string' ? new Date(v) : v instanceof Date ? v : null;
        if (!d || isNaN(d.getTime())) return '';
        return d.toLocaleDateString('tr-TR');
    };

    React.useEffect(() => {
        if (visible) {
            setPage(1);
            setRows(10);
        }
    }, [visible, itemCode, chassisNumber]);

    const handleApplyFilters = (f) => {
        setFilters((prev) => ({
            ...prev,
            chassisNumber: f.chassisNumber ?? prev.chassisNumber,
            dealerId: f.dealerId,
            claimDate: f.claimDate,
            failureDate: f.failureDate,
            failureStartDate: f.failureStartDate,
            failureEndDate: f.failureEndDate,
            status: f.status,
            models: f.models,
            countryCode: f.countryCode,
            isScrap: f.isScrap,
            warrantyType: f.warrantyType
        }));
        setPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            chassisNumber: chassisNumber || undefined,
            dealerId: undefined,
            claimDate: undefined,
            failureDate: undefined,
            failureStartDate: undefined,
            failureEndDate: undefined,
            status: undefined,
            models: undefined,
            countryCode: undefined,
            isScrap: undefined,
            warrantyType: undefined
        });
        setPage(1);
    };
    const statusKeyMap = {
        10: 'drafts',
        15: 'returned',
        20: 'waiting',
        30: 'accepted',
        40: 'rejected'
    };

    const renderStatus = (row) => {
        const raw = row?.Status;

        if (raw == null) return '';

        // number ya da string gelebilir, normalize edelim
        const numeric = typeof raw === 'string' ? parseInt(raw, 10) : raw;

        const key = statusKeyMap[numeric];
        if (!key) return raw; // tanımsız statü gelirse fallback olarak raw göster

        return th(`warrantyReports.details.statusText.${key}`);
    };

    const renderIsScrap = (row) => {
        const raw = row?.IsScrap;
        if (raw == null) return '';

        const numeric = typeof raw === 'string' ? parseInt(raw, 10) : raw;
        if (numeric === 1) return th('warrantyClaims.detail.partDialog.keepPart');
        if (numeric === 2) return th('warrantyClaims.detail.partDialog.scrapPart');
        return raw;
    };

    const handleClaimNavigate = (row) => {
        if (!row?.ClaimId) return;
        navigate(`/warranty-management/warranty-requests/claims/${row.ClaimId}`);
    };

    const renderClaimNo = (row) => {
        if (!row?.WarrClaimsNo) return '';
        return (
            <span
                onClick={() => handleClaimNavigate(row)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClaimNavigate(row);
                    }
                }}
                style={{
                    color: '#0d6efd',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                }}
            >
                {row.WarrClaimsNo}
            </span>
        );
    };

    return (
        <Dialog header={`${th('warrantyReports.details.title')} - ${itemCode ?? ''}`} visible={visible} style={{ width: '80vw', maxWidth: 1100 }} onHide={onHide} modal>
            <PartReplacementDetailsFilter initialChassisNumber={chassisNumber} onApply={handleApplyFilters} onReset={handleResetFilters} isDealer={isDealer} />{' '}
            <StickyScrollTable deps={[sortedData, visible]} scrollBarPosition="top">
            <DataTable
                value={sortedData}
                lazy
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={total}
                onPage={onPageChange}
                loading={isFetching}
                rowsPerPageOptions={[10, 25, 50, 100]}
                showGridlines
                sortMode="single"
                onSort={handleSort}
                sortField={sortField || undefined}
                sortOrder={sortOrder || 0}
                tableStyle={{ minWidth: '50rem' }}
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            >
                <Column field="DealerFullName" header={th('warrantyReports.details.columns.DealerFullName')} sortable />
                <Column field="WarrClaimsNo" header={th('warrantyReports.details.columns.WarrClaimsNo')} body={renderClaimNo} sortable />
                <Column field="ChassisNo" header={th('warrantyReports.details.columns.ChassisNo')} sortable />
                <Column field="Quantity" header={th('warrantyReports.details.columns.Quantity')} sortable />
                <Column field="ClaimDate" header={th('warrantyReports.details.columns.ClaimDate')} body={(row) => formatDate(row.ClaimDate)} sortable />
                <Column field="FailureDate" header={th('warrantyReports.details.columns.FailureDate')} body={(row) => formatDate(row.FailureDate)} sortable />
                <Column field="WarrTypeText" header={th('warrantyReports.details.columns.WarrTypeText')} sortable />
                <Column field="CountryName" header={th('warrantyReports.details.columns.CountryName')} sortable />
                <Column field="Status" header={th('warrantyReports.details.columns.Status')} body={renderStatus} sortable />
                <Column field="ItemPrice" header={th('warrantyReports.details.columns.ItemPrice')} sortable />
                <Column field="ItemPriceCurr" header={th('warrantyReports.details.columns.ItemPriceCurr')} sortable />
                <Column field="Model" header={th('warrantyReports.details.columns.Model')} sortable />
                <Column field="IsScrap" header={th('warrantyClaims.detail.partDialog.isScrap')} body={renderIsScrap} sortable />
                <Column field="WarrantyTypeText" header={th('warrantyReports.details.columns.WarrantyType')} sortable />
            </DataTable>
            </StickyScrollTable>
        </Dialog>
    );
}
